// Observability emitters for studio.client. Greppable, structured log
// helpers used by W12 (Track 4a) to instrument the renderer for the
// post-Shopify-incident diagnostics initiative.
//
// USER STORY (referenced by every test in observability.test.ts and by
// every call site that imports from this file): when an Iris auto-update
// pushes a broken bridge to a customer (the Shopify incident), the
// support engineer's only path to root cause is the renderer log file.
// Today most failure modes are silent — a tool greys out, an API call
// quietly dies, the bridge bounces to "offline" and back — and nothing
// distinguishes a broken upgrade from normal operation. These helpers
// emit a fixed, greppable line per event so support can `grep tool_disabled`
// or `grep api.response endpoint=billing-info` against the bundle.
//
// Per rule: stay-in-scope (ai.knowledge/global) — every emitter forwards
// to the canonical `logger` (#~/logger/index.js), which already redacts
// secrets via W3 and stamps boot_id. We do NOT introduce console.* calls
// and we do NOT modify the underlying logger.
//
// Per rule: ai-code-requires-tests + tests-require-user-story-comment
// (ai.knowledge/global) — tests live in observability.test.ts, each
// test annotated with the Shopify-incident user story.
//
// Per rule: think-in-systems (ai.knowledge/global) — one helper per
// event class. Call sites stay one-liners. The shape of each line is
// owned by THIS file so support can rely on it being consistent across
// the renderer.
//
// Per knowledge: studio.client/use-canonical-logger-not-console.md —
// uses `import { logger } from '#~/logger/index.js'`. Never raw console.

import { logger } from './index.js';

// =====================================================================
// 1. Diagnostic-path API responses
// =====================================================================
//
// Diagnostic endpoints are the SHORT LIST of routes whose full body is
// safe to log (after W3 redaction). These are the routes a support
// engineer needs to see verbatim to root-cause a customer issue:
//
//   /room/billing-info  -> plan limits, usage, overage flags
//   /devices            -> device registry shape, drivers, paths
//   /metrics            -> agent counter snapshot
//   /calibration        -> per-camera calibration state
//
// Anything NOT in this list is a non-diagnostic endpoint and should
// instead use logApiResponseShape() — which only emits the key set and
// value types, not the body. This is the bandwidth + privacy boundary.
//
// The boundary is explicit (not a regex) so a future engineer who adds
// /payments or /sso can't accidentally widen the diagnostic list.

const DIAGNOSTIC_ENDPOINTS: ReadonlySet<string> = new Set([
  'billing-info',
  'devices',
  'metrics',
  'calibration'
]);

/**
 * Returns true if the endpoint is on the diagnostic short list and
 * therefore eligible for full-body logging (W3 redaction still applies
 * via the canonical logger). False for everything else.
 *
 * Endpoints are matched by their canonical short name, not the URL
 * path — the caller passes 'billing-info' not '/room/abc/billing-info'.
 * This decouples the diagnostic list from URL evolution.
 */
export const isDiagnosticEndpoint = (endpoint: string): boolean =>
  DIAGNOSTIC_ENDPOINTS.has(endpoint);

/**
 * Log a diagnostic-path API response with full body.
 *
 * The full body is forwarded through the canonical logger which applies
 * W3 redaction (JWT, OAuth, Stripe, API keys, email, paths) before any
 * sink writes the line. The format is:
 *
 *   api.response endpoint=<name> status=<code> elapsed_ms=<ms>
 *
 * with the body attached as a structured trailing arg so the support
 * engineer can grep on `endpoint=billing-info` to filter.
 *
 * If the endpoint is NOT on the diagnostic list, this fail-opens to
 * logApiResponseShape() so a typo'd endpoint name doesn't accidentally
 * leak a payload — the caller's intent ("this is diagnostic") is
 * cross-checked against the list here.
 */
export const logApiResponse = (
  endpoint: string,
  status: number,
  elapsedMs: number,
  body: unknown
): void => {
  // Wrap in try/catch per W12 hard constraint: observability must never
  // cause a render-time exception. A bad input here would otherwise
  // bubble up through the API path that called us.
  try {
    if (!isDiagnosticEndpoint(endpoint)) {
      // Caller asked for diagnostic logging on a non-diagnostic endpoint.
      // Fall back to shape-only — never widen the diagnostic surface
      // by accident.
      logApiResponseShape(endpoint, status, elapsedMs, body);
      return;
    }

    logger.info(
      `api.response endpoint=${endpoint} status=${status} elapsed_ms=${elapsedMs}`,
      // Body is the structured trailing arg. The canonical logger's
      // W3 redactArgs() pass scrubs strings inside; non-string args
      // (objects, Errors) pass through with redaction applied to their
      // serialized form by the underlying console.log call.
      { body }
    );
  } catch {
    // Swallow — observability is best-effort.
  }
};

/**
 * Log a non-diagnostic API response as SHAPE only — the key set of the
 * top-level body plus the value type for each key. No values.
 *
 * Format:
 *   api.response.shape endpoint=<name> status=<code> elapsed_ms=<ms>
 *   keys=<comma-separated> types=<key:type,...>
 *
 * Designed for hot endpoints (state-sync mutations, presence pings,
 * tokens) where logging the full body would either flood the bundle or
 * leak credentials we haven't W3-redacted yet. Shape gives support
 * enough to confirm "the response had the field we expected" without
 * forcing us to widen the diagnostic list.
 */
export const logApiResponseShape = (
  endpoint: string,
  status: number,
  elapsedMs: number,
  body: unknown
): void => {
  try {
    const shape = describeShape(body);
    logger.info(
      `api.response.shape endpoint=${endpoint} status=${status} elapsed_ms=${elapsedMs} keys=[${shape.keys.join(',')}] types={${shape.types}}`
    );
  } catch {
    // Swallow.
  }
};

/**
 * Compute the shape of an arbitrary value: top-level keys + the type
 * of each value. Returns { keys: [], types: '' } for non-object inputs
 * (primitives, null, arrays at the top level — arrays log their length
 * and the first element's type so support can spot empty responses).
 *
 * Exported only for the test file; not part of the public surface.
 */
export const describeShape = (value: unknown): { keys: string[]; types: string } => {
  if (value === null || value === undefined) {
    return { keys: [], types: '' };
  }

  if (Array.isArray(value)) {
    // Top-level array — describe length + first-element type.
    const elemType = value.length > 0 ? typeOf(value[0]) : 'empty';
    return {
      keys: [`array[${value.length}]`],
      types: `[0]:${elemType}`
    };
  }

  if (typeof value !== 'object') {
    // Primitive — no shape, just the type.
    return { keys: [], types: typeOf(value) };
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const types = keys.map((k) => `${k}:${typeOf(obj[k])}`).join(',');
  return { keys, types };
};

/**
 * Narrow JS typeof that distinguishes null/array/object — typeof null
 * is 'object' which is rarely useful in a shape report.
 */
const typeOf = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return `array[${value.length}]`;
  }
  return typeof value;
};

// =====================================================================
// 2. State-sync events
// =====================================================================
//
// State-sync events fire often (every authority push, every optimistic
// mutation roundtrip). Logging the full payload would be a bandwidth
// disaster — a typical session has thousands of state-sync transactions.
// Instead we log type + a short list of "key fields" — what changed at
// the top level and which authority flag fired.

/**
 * Log a state-sync transaction event.
 *
 * Format:
 *   state.sync agent=<id> type=<reset|init|resync|authority|optimistic|tx>
 *   keys=[<top-level-control-and-status-keys>]
 *
 * No payload values — this fires on the hot path. Support engineers
 * grep `state.sync agent=abc123` to reconstruct the event sequence
 * for one agent without drowning in payload bytes.
 */
export const logStateSyncEvent = (
  agentId: string,
  // Accept `boolean | undefined` rather than `?:` so callers passing
  // a transaction object whose flag fields are typed as `boolean | undefined`
  // (e.g. broker.ts mutationCallback `tx.isReset` etc.) compile under
  // `exactOptionalPropertyTypes: true`. The diagnostic intent is to log
  // every flag position including its explicit-undefined state, so widening
  // here is correct — we never differentiate "missing" from "undefined" inside.
  // Per `stay-in-scope` (ai.knowledge/global) — minimal widening, no other
  // contract changes.
  flags: {
    isReset?: boolean | undefined;
    isInit?: boolean | undefined;
    isResync?: boolean | undefined;
    isAuthority?: boolean | undefined;
    isOptimistic?: boolean | undefined;
  },
  topLevelKeys: string[]
): void => {
  try {
    // Pick the strongest semantic flag in priority order so the line
    // is canonical: reset > init > resync > authority > optimistic.
    // Multiple flags can be true (init + authority), but support
    // wants ONE label per event for grep cardinality.
    let type = 'tx';
    if (flags.isReset) type = 'reset';
    else if (flags.isInit) type = 'init';
    else if (flags.isResync) type = 'resync';
    else if (flags.isAuthority) type = 'authority';
    else if (flags.isOptimistic) type = 'optimistic';

    logger.info(`state.sync agent=${agentId} type=${type} keys=[${topLevelKeys.join(',')}]`);
  } catch {
    // Swallow.
  }
};

// =====================================================================
// 3. Tool-enable gating
// =====================================================================
//
// "Tool gating" — the logic that disables a tool (auto-tracking, fast
// frame, joystick, presets, etc.) for a specific reason. The plan
// acknowledges this site is decentralized in studio.client (search the
// codebase: there's no single Tool.isEnabled(reason) function). Per
// the W12 acceptance:
//
//   "If a single function does not exist, instrument at the closest
//    aggregation point and document in the PR description (NOT a
//    refactor)".
//
// We instrument via this single helper, called from every gating site.
// One log per disabled state with a fixed `reason` enum. Support greps
// `tool_disabled tool=tracking reason=calibration_missing` to see why
// a customer's auto-tracking was greyed out.
//
// PR DOCUMENTATION (per acceptance criterion):
//   The tool-gating site IS NOT a single function. The closest
//   aggregation point is the per-tool React component's "guard" — for
//   example studio/client/packages/frontend/src/room/components/
//   auto-tracking/auto-tracking.tsx::Guard, which decides between
//   <NotOnPlan>, <Uncalibrated>, <Unsupported>, and the live tool. We
//   instrument at each guard with a one-line logToolDisabled() call so
//   the line shape is canonical regardless of which guard fires it.
//   The decentralization is acceptable per plan (Risks/Mitigations row
//   "Tool-gating site not centralized").

/**
 * Canonical reason codes for a disabled tool. Adding a new reason
 * requires (a) adding the literal here and (b) adding a test in
 * observability.test.ts. The string literal IS the grep target —
 * don't ad-hoc free-form reasons at call sites.
 */
export type ToolDisabledReason =
  | 'calibration_missing'
  | 'driver_unsupported'
  // ENG-4495 US5: a driverless ("Other/Other") model blocks auto-tracking regardless of
  // calibration state — a distinct grep target so support can tell it apart from the others.
  | 'driver_generic'
  | 'bridge_offline'
  | 'device_unknown'
  | 'plan_limit'
  | 'feature_flag_off';

/**
 * Log a tool-disabled gating event.
 *
 * Format:
 *   tool_disabled tool=<name> reason=<reason-code>
 *
 * Idempotency: callers SHOULD wrap this in a useEffect or a state-
 * change check so we don't emit the same line on every render. This
 * helper does NOT dedupe — callers that fire on every render will
 * flood the log. Each existing call site below uses a useEffect with
 * the reason as a dep so the line fires once per state transition.
 */
export const logToolDisabled = (tool: string, reason: ToolDisabledReason): void => {
  try {
    logger.info(`tool_disabled tool=${tool} reason=${reason}`);
  } catch {
    // Swallow.
  }
};

// =====================================================================
// 4. Bridge connection-state transitions
// =====================================================================
//
// Bridge connection state — every transition (online, offline,
// reconnecting). Today the watchdog only fires posthog analytics
// events; the renderer log file is silent. Support cannot reconstruct
// "did the bridge actually disconnect, or did the customer think it did"
// without this.

export type BridgeConnState = 'online' | 'offline' | 'reconnecting' | 'idle' | 'booting';

/**
 * Log a bridge connection-state transition.
 *
 * Format:
 *   bridge.conn agent=<id> from=<state> to=<state>
 *
 * `from` may be 'unknown' for the first transition of the session —
 * that's a valid state, not an error.
 */
export const logBridgeConnState = (
  agentId: string,
  from: BridgeConnState | 'unknown',
  to: BridgeConnState
): void => {
  try {
    logger.info(`bridge.conn agent=${agentId} from=${from} to=${to}`);
  } catch {
    // Swallow.
  }
};

// =====================================================================
// 5. Last-known-good cache load on page boot
// =====================================================================
//
// The renderer reads a cached snapshot at boot (loadSnapshot in
// agent/broker.ts via getData() in api/storage). Today the load is
// silent — if the cache is empty, stale, or corrupt, support has to
// guess. A single line at boot:
//
//   cache.load agent=<id> cache_age_ms=<n> keys=[<top-level-keys>]
//
// is enough to confirm the renderer started from real cached state vs
// an empty snapshot.

/**
 * Log a successful saved-state load. loadSnapshot() shares the successful
 * promise within a proxy generation, including empty snapshots. A reset or
 * account change retires that cache, so its next successful load logs again.
 */
export const logCacheLoad = (agentId: string, cacheAgeMs: number, topLevelKeys: string[]): void => {
  try {
    logger.info(
      `cache.load agent=${agentId} cache_age_ms=${cacheAgeMs} keys=[${topLevelKeys.join(',')}]`
    );
  } catch {
    // Swallow.
  }
};
